import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import ClearIcon from "@mui/icons-material/Clear";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ElectricCarIcon from "@mui/icons-material/ElectricCar";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import AccessibleIcon from "@mui/icons-material/Accessible";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import BlockIcon from "@mui/icons-material/Block";

import Config from "./config";

type MapConfigTypes = {
  defaultTileUrl: string;
  marker: {
    weight: {
      [key: string]: number;
    };
    color: {
      [key: string]: string;
    };
    fillColor: {
      [key: string]: string;
    };
    radius: {
      [key: string]: number;
    };
  };
  polyline: {
    stroke: {
      [key: string]: number;
    };
  };
  flow: {
    defaultMaxDaysAllowed: number;
    colorFactory: string[];
    area: {
      color: {
        disabled: string;
        unselected: string;
        selected: string;
      };
      fillColor: {
        unselected: string;
        selected: string;
      };
      fillOpacity: {
        selected: number;
        unselected: number;
        disabled: number;
      };
    };
    point: {
      color: {
        unselected: string;
        selected: string;
      };
      fillColor: {
        unselected: string;
        selected: string;
      };
      weight: {
        [key: string]: number;
      };
      radius: {
        [key: string]: number;
      };
    };
    path: {
      color: {
        disabled: string;
        unselected: string;
        selected: string;
      };
      stroke: {
        [key: string]: number;
      };
      arrow: {
        offset?: string;
        endOffset?: string;
        selected_color?: string;
        repeat?: string;
        pixelSize?: number;
        color?: {
          disabled: string;
          unselected: string;
          selected: string;
        };
      };
    };
  };
  heatmap: {
    config: {
      backgroundColor: string | undefined;
      gradient: {
        0: string;
        0.55: string;
        0.65: string;
        "0.80": string;
        "0.90": string;
        "1.0": string;
      };
      radius: number;
      opacity: number | undefined; // override maxOpacity/minOpacity if defined
      maxOpacity: number;
      minOpacity: number;
      blur: number;
    };
  };
  park: {
    defaultMaxDaysAllowed: number; // default value in days
    defaultMaxDaysAllowedHeatmapOccupancy: number; // default value in days
  };
  pedestrian: {
    defaultMaxDaysAllowed: number;
  };
  customAnalysis: {
    defaultMaxDaysAllowed: number;
  };
  iot: {
    display: {
      color: {
        [key: string]: string;
      };
      symbol: {
        [key: string]: any;
      };
    };
  };
  timePickers: {
    tempo: number; // tempo between each api call in ms
  };
  iconByType: {
    [key: string]: {
      [key: string]: any;
    };
  };
};

const MapConfig: MapConfigTypes = {
  defaultTileUrl: `https://api.mapbox.com/styles/v1/mapbox/streets-v9/tiles/256/{z}/{x}/{y}?access_token=${Config.mapboxToken}`,
  marker: {
    weight: {
      selected: 4,
      hover: 4,
      default: 0,
      probably_car: 0,
      normal: 0,
      paying: 0,
      time_limited: 0,
      delivery: 3,
      forbidden: 3,
      prm: 3,
      free: 0,
      street_access: 3,
      carsharing: 3,
      chargin_station: 3,
      drop_off: 0,
      cash_transport: 3,
      taxi: 3,
      drive_pickup: 3,
      drive_pickup_large: 3,
      drive_queue: 3,
      service: 3,
    },
    color: {
      default: "black",
      selected: "#3C6382",
      hover: "#EB1547",
      normal: "black",
      paying: "black",
      time_limited: "black",
      delivery: "#fff046",
      forbidden: "#fff046",
      prm: "#4ba9ff",
      free: "black",
      street_access: "#fff046",
      carsharing: "#14c9b3",
      chargin_station: "#99ffb6",
      drop_off: "black",
      cash_transport: "#fff046",
      taxi: "#56647a",
      drive_pickup: "#34a6af",
      drive_pickup_large: "#34a6af",
      drive_queue: "#ffffff",
      service: "#d5d5d5",
    },
    fillColor: {
      default: "gray",
      probably_car: "#EB1547",
      car: "#EB1547",
      spot: "#27AE60",
      area: "#27AE60",
      temporary_car: "#EB1547",
      temporary_spot: "#27AE60",
      unavailable: "#B4B4B4",
      idle_spot: "#27AE60",
    },
    radius: {
      default: 2,
      zoom0: 0.001,
      zoom1: 0.001,
      zoom2: 0.001,
      zoom3: 0.001,
      zoom4: 0.001,
      zoom5: 0.001,
      zoom6: 0.001,
      zoom7: 0.001,
      zoom8: 0.001,
      zoom9: 0.001,
      zoom10: 0.001,
      zoom11: 0.001,
      zoom12: 0.01,
      zoom13: 0.01,
      zoom14: 0.1,
      zoom15: 1,
      zoom16: 1,
      zoom17: 2,
      zoom18: 3,
      zoom19: 5,
      zoom20: 8,
      zoom21: 9,
      zoom22: 11,
      zoom23: 11,
      zoom24: 12,
      zoom25: 12,
    },
  },
  polyline: {
    stroke: {
      default: 7,
      zoom0: 1,
      zoom1: 1,
      zoom2: 1,
      zoom3: 1,
      zoom4: 1,
      zoom5: 1,
      zoom6: 1,
      zoom7: 1,
      zoom8: 1,
      zoom9: 1,
      zoom10: 1,
      zoom11: 1,
      zoom12: 1,
      zoom13: 2,
      zoom14: 2,
      zoom15: 3,
      zoom16: 4,
      zoom17: 7,
      zoom18: 8,
      zoom19: 9,
      zoom20: 9,
      zoom21: 10,
      zoom22: 10,
      zoom23: 11,
      zoom24: 11,
      zoom25: 12,
    },
  },
  iot: {
    display: {
      color: {
        default: "white",
        black: "#ecebeb",
        yellow: "#e8cb50",
        red: "#E55039",
        green: "#27AE60",
        purple: "#9b59b6",
        amber: "#ffbf00",
        white: "#ecebeb",
      },
      symbol: {
        right_arrow: ArrowForwardIcon,
        left_arrow: ArrowBackIcon,
        upward_arrow: ArrowUpwardIcon,
        downward_arrow: ArrowDownwardIcon,
        lower_left_diagonal_arrow: SouthWestIcon,
        upper_left_diagonal_arrow: NorthWestIcon,
        upper_right_diagonal_arrow: NorthEastIcon,
        lower_right_diagonal_arrow: SouthEastIcon,
        cross: ClearIcon,
        no_entry_sign: RemoveCircleOutlineIcon,
        dead_end: BlockIcon,
        parking_sign: LocalParkingIcon,
        charging_station: ElectricCarIcon,
        prm: AccessibleIcon,
        motorcycle: TwoWheelerIcon,
      },
    },
  },
  flow: {
    defaultMaxDaysAllowed: 1,
    colorFactory: [
      "#74b9ff",
      "#ffeaa7",
      "#fdcb6e",
      "#ff7675",
      "#e17055",
      "#fab1a0",
      "#00b894",
      "#00cec9",
      "#0984e3",
      "#96ceb4",
      "#ffeead",
      "#ffcc5c",
    ],
    area: {
      color: {
        disabled: "#95a5a6",
        unselected: "#7D7D7D",
        selected: "#69A73B",
      },
      fillColor: {
        unselected: "white",
        selected: "#69A73B",
      },
      fillOpacity: {
        selected: 0.3,
        unselected: 0.3,
        disabled: 0.15,
      },
    },
    point: {
      color: {
        unselected: "white",
        selected: "white",
      },
      fillColor: {
        unselected: "#7D7D7D",
        selected: "#69A73B",
      },
      weight: {
        default: 3,
        zoom14: 1,
        zoom15: 1,
        zoom16: 1,
        zoom17: 2,
        zoom18: 2,
        zoom19: 3,
        zoom20: 3,
      },
      radius: {
        default: 12,
        zoom12: 0.5,
        zoom13: 1,
        zoom14: 2,
        zoom15: 3,
        zoom16: 4,
        zoom17: 8,
        zoom18: 12,
        zoom19: 14,
        zoom20: 22,
      },
    },
    path: {
      color: {
        disabled: "#95a5a6",
        unselected: "rgba(230,230,230,0.5)",
        selected: "yellow",
      },
      stroke: {
        default: 4,
        // zoom14: 1,
      },
      arrow: {
        offset: "8%",
        endOffset: "8%",
        repeat: "10%",
        pixelSize: 12,
        color: {
          disabled: "#95a5a6",
          unselected: "rgba(230,230,230,0.5)",
          selected: "yellow",
        },
      },
    },
  },
  heatmap: {
    config: {
      backgroundColor: undefined,
      gradient: {
        0: "rgb(128,208,8)",
        0.55: "rgb(128,208,8)",
        0.65: "rgb(255,215,0)",
        "0.80": "rgb(255,165,0)",
        "0.90": "rgb(255,140,0)",
        "1.0": "rgb(255,51,0)",
      },
      radius: 0.00032,
      opacity: undefined, // override maxOpacity/minOpacity if defined
      maxOpacity: 0.9,
      minOpacity: 0.3,
      blur: 0.85,
    },
  },
  park: {
    defaultMaxDaysAllowed: 7, // default value in days
    defaultMaxDaysAllowedHeatmapOccupancy: 365, // default value in days
  },
  pedestrian: {
    defaultMaxDaysAllowed: 7,
  },
  customAnalysis: {
    defaultMaxDaysAllowed: 1,
  },
  timePickers: {
    tempo: 1400, // tempo between each api call in ms
  },
  iconByType: {
    live: {
      area: {
        PARK: DirectionsCarIcon,
        PARKANDFLOW: DirectionsCarIcon,
        PEDESTRIANANDFLOW: DirectionsWalkIcon,
        FLOW: SyncAltIcon,
        QUEUEMANAGEMENT: AccessAlarmIcon,
      },
      point: {
        PARK: DirectionsCarIcon,
        PARKANDFLOW: DirectionsCarIcon,
        PEDESTRIANANDFLOW: DirectionsWalkIcon,
        FLOW: SwapVertIcon,
      },
      path: {
        PARKANDFLOW: AccessAlarmIcon,
        PEDESTRIANANDFLOW: AccessAlarmIcon,
        FLOW: AccessAlarmIcon,
        TRAFFIC: DirectionsCarIcon,
      },
    },
    flow: {
      area: {
        PARKANDFLOW: SyncAltIcon,
        PEDESTRIANANDFLOW: SyncAltIcon,
        FLOW: SyncAltIcon,
      },
      point: {
        FLOW: SwapVertIcon,
      },
      path: {
        PARKANDFLOW: AccessAlarmIcon,
        PEDESTRIANANDFLOW: AccessAlarmIcon,
        FLOW: AccessAlarmIcon,
        TRAFFIC: DirectionsCarIcon,
      },
    },
    park: {
      area: {
        PARK: DirectionsCarIcon,
        PARKANDFLOW: DirectionsCarIcon,
      },
    },
    pedestrian: {
      area: {
        PEDESTRIANANDFLOW: DirectionsWalkIcon,
        QUEUEMANAGEMENT: AccessAlarmIcon,
      },
    },
  },
};

export default MapConfig;
